/**
 * Sign in with a CLIRNET account. Credentials go to /api/auth/login, which
 * forwards them to the CLIRNET auth service and keeps the session in an
 * HttpOnly cookie. After signing in, the user returns to the page they asked for.
 *
 * Layout: a white rounded container on the pale page, the form on the left and,
 * on wide screens, the brand showcase on the right (login/LoginShowcase.jsx).
 */

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../api/client.js";
import { Button, Field, Icon, IconButton, Input } from "../components/ui/index.js";
import LoginShowcase from "./login/LoginShowcase.jsx";

/** Only same-app paths are allowed as a return target. */
function safeNext(raw) {
  return typeof raw === "string" && raw.startsWith("/") && !raw.startsWith("//") && !raw.startsWith("/login") ? raw : "/";
}

function BrandMark({ className = "size-9" }) {
  return (
    <span aria-hidden className={`grid shrink-0 place-items-center rounded-[12px] bg-gradient-to-br from-[#0f2c60] to-[#3c7abe] text-white ${className}`}>
      <svg viewBox="0 0 32 32" className="size-[60%]" fill="none" stroke="currentColor" strokeWidth={3.2} strokeLinecap="round">
        <path d="M8 23V15M14 23V9M20 23v-6M26 23V12" />
      </svg>
    </span>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qc = useQueryClient();
  const next = safeNext(new URLSearchParams(search).get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiPost("/auth/login", { email: email.trim(), password });
      qc.clear();
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message || "Sign-in failed.");
      setBusy(false);
    }
  };

  return (
    // The page behind the container: pale grey with a faint brand-blue wash from two corners.
    <div className="flex min-h-screen items-center justify-center bg-surface-0 bg-[radial-gradient(60%_55%_at_0%_0%,rgb(15_44_96/0.10),transparent_70%),radial-gradient(55%_50%_at_100%_100%,rgb(60_122_190/0.14),transparent_70%),linear-gradient(160deg,#eef1f7_0%,#f3f4f7_50%,#edf2f9_100%)] p-0 sm:p-6">
      <div className="grid min-h-screen w-full max-w-[1200px] bg-surface-1 p-3 sm:min-h-[min(760px,calc(100vh-48px))] sm:rounded-[28px] sm:shadow-[0_1px_2px_rgb(16_24_40/0.04),0_30px_80px_-30px_rgb(16_24_40/0.25)] lg:grid-cols-2">
        <main className="flex flex-col px-5 py-6 sm:px-10 sm:py-8 xl:px-14">
          <div className="flex items-center gap-3">
            <BrandMark />
            <div>
              <p className="text-[15px] font-semibold tracking-tight text-ink">CLIRNET</p>
              <p className="text-[12px] text-ink-3">Content analytics</p>
            </div>
          </div>

          <div className="flex flex-1 items-center py-10">
            <form onSubmit={submit} className="w-full max-w-[400px] animate-rise" noValidate>
              <h1 className="text-[32px] leading-tight font-semibold tracking-[-0.025em] text-ink">Welcome back</h1>
              <p className="mt-2 text-[14px] text-ink-3">Sign in with your CLIRNET account to open Content Analytics.</p>

              <div className="mt-8 flex flex-col gap-4">
                <Field label="Email">
                  <Input size="lg" icon="mail" type="email" autoComplete="username" required placeholder="name@clirnet.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label="Password">
                  <Input
                    size="lg"
                    icon="key"
                    type={reveal ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    trailing={<IconButton icon={reveal ? "eyeOff" : "eye"} label={reveal ? "Hide password" : "Show password"} size="sm" aria-pressed={reveal} onClick={() => setReveal((r) => !r)} />}
                  />
                </Field>
              </div>

              {error && (
                <p
                  role="alert"
                  className="mt-4 flex animate-fade-in items-start gap-2 rounded-control border px-3 py-2.5 text-[12.5px] text-ink"
                  style={{
                    borderColor: "rgb(184 51 43 / 0.25)",
                    background: "var(--status-critical-bg)",
                  }}
                >
                  <span style={{ color: "var(--status-critical)" }}>
                    <Icon name="alert" className="mt-px size-4" />
                  </span>
                  {error}
                </p>
              )}

              <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!email.trim() || !password} iconRight={busy ? undefined : "arrowRight"} className="mt-6">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-ink-3">
            <p className="flex items-center gap-1.5">
              <Icon name="lock" className="size-3.5" />
              Your session is kept in a secure, HttpOnly cookie.
            </p>
            <p>© {new Date().getFullYear()} CLIRNET</p>
          </div>
        </main>

        <LoginShowcase />
      </div>
    </div>
  );
}
